/**
 * CORE-820 — who may publish a finished Live-session recording to «Медиа» and
 * manage that archive entry afterwards.
 *
 * The affordance lives in two places, both opening the same
 * `PublishToMediaModal`:
 *
 *  - the master-completion screen of `LiveRoomScreen` (CORE-588, screen 26) — a
 *    «Опубликовать в Медиа» CTA once the recording is ready;
 *  - the «Мои комнаты» row menu in `MyRoomsSection` — a «Публикация в Медиа»
 *    action on an archived session that still owns a recording.
 *
 * Both were gated inline on the same frozen fields. This module lifts that gate
 * into one pure, testable policy — the sibling of `roomRecordingControlPolicy`
 * (CORE-760), `roomPracticeControlPolicy` (CORE-795) and `roomBroadcastPolicy`
 * — so the availability rules for archive/publish are asserted in one place
 * instead of being re-derived at each call site.
 *
 * ── Why gate on the capability, not the frozen role ─────────────────────────
 *
 * CORE-768 §7.1 #5 (owner, 11.08.2026) sanctioned a real capability key,
 * `session.recording.publish_media`, as a `RoleOnly` matrix entry: publishing a
 * recording to «Медиа» is reserved for the confirmed master role and never
 * unlocked by levelling up, aligned with `session.discovery.platform`. The
 * backend has since added the key to its catalog (`LiveSessionCapabilities`) AND
 * ships it inside the master's FROZEN `capabilities[]` snapshot — verified on the
 * dev stand: a master session's `CreatorCapabilities` carries
 * `session.recording.publish_media`.
 *
 * An earlier revision of this module gated on the frozen `creatorRole === 'master'`
 * instead, on the (then-true) assumption that no such key existed. That
 * assumption is now false, and QA rejected it (QA-FAIL 14.08.2026): deciding on
 * the frozen role instead of the actual capability means a later delegation or
 * revocation of the right has no effect on the UI. So the gate is the presence
 * of `session.recording.publish_media` in the frozen snapshot — the same source
 * of truth every other Live affordance reads, and the one the AC demands
 * («доступность … определяется session.recording.publish_media из замороженного
 * снимка прав»).
 *
 * The caller resolves the key off the snapshot through
 * `resolveFrozenCap(unifiedLive, room.capabilities, 'session.recording.publish_media', false)`
 * (the shared back-compat guard) and passes the result as `hasPublishCapability`.
 * That helper honours the snapshot only when the unified flag is ON and the room
 * actually carries a non-empty one; otherwise it returns the `false` default —
 * so this policy never has to know about the flag or the snapshot shape.
 *
 * This is contract-compliant, NOT a violation of the CORE-769 §0 bans:
 *
 *  - The capability array IS the FROZEN rights snapshot (§5.1), decided by the
 *    server at session creation and only READ here. It is NOT the global
 *    `user.isMaster` (ban #2 «isMaster как единственный источник») and NOT
 *    re-derived from a level code (ban #1). No level / `creatorLevel` term
 *    enters this decision (ban #3, §1.1).
 *  - The RoleOnly semantics are enforced server-side; the client simply reads
 *    what the frozen snapshot granted. If publish is ever delegated to a co-host
 *    (a per-session grant), the snapshot carries the key and this same policy
 *    opens the action — nothing here changes.
 *
 * ── Safe behaviour (AC: «при выключенном флаге и при ошибке прав — безопасно») ─
 *
 * `hasPublishCapability` folds in the unified-Live gate and the snapshot check
 * at the call site (via `resolveFrozenCap(..., false)`):
 *
 *  - Flag OFF, or a missing / empty snapshot (the «ошибка прав» case) → the
 *    helper returns false → the action hides. Fail-closed.
 *  - A regular-user session (snapshot without the key) → false → hides.
 *  - Legacy quick rooms (no snapshot at all) → false → hides.
 */

import type { WatchPartyRecordingState } from '../types/auth';

export interface PublishToMediaInput {
    /**
     * Whether the session's FROZEN rights snapshot grants
     * `session.recording.publish_media`. The caller resolves this off the frozen
     * `capabilities[]` via
     * `resolveFrozenCap(unifiedLive, room.capabilities, 'session.recording.publish_media', false)`,
     * so a flag-off / missing / empty snapshot yields false (fail-closed) and a
     * regular-user session (no key) yields false. A confirmed master's snapshot
     * carries the key; a co-host it was delegated to would too. This is the
     * frozen rights snapshot (§5.1), NOT the viewer's global `isMaster` and NOT a
     * level code.
     */
    hasPublishCapability: boolean;
    /** Live recording state; `'None'` / null means nothing has been captured. */
    recordingState?: WatchPartyRecordingState | null;
    /**
     * A published-recording URL already on the archive row. Present after a
     * recording is promoted even once `recordingState` has been reset to
     * `'None'`, so the manage action stays reachable for the archive entry.
     */
    playbackUrl?: string | null;
}

/**
 * True when this session actually owns a recording worth publishing / managing:
 * a live capture in flight or finished (`recordingState !== 'None'`), or an
 * already-published archive URL.
 */
function hasRecordingToPublish(
    recordingState?: WatchPartyRecordingState | null,
    playbackUrl?: string | null,
): boolean {
    return !!playbackUrl || (recordingState != null && recordingState !== 'None');
}

/**
 * True when the «Опубликовать в Медиа» / «Публикация в Медиа» affordance should
 * be shown for this session: the frozen snapshot grants
 * `session.recording.publish_media` and a recording actually exists. Any other
 * case (flag off, broken/empty snapshot, session without the key, no recording)
 * hides the action.
 */
export function computeCanPublishToMedia({
    hasPublishCapability,
    recordingState,
    playbackUrl,
}: PublishToMediaInput): boolean {
    return (
        hasPublishCapability
        && hasRecordingToPublish(recordingState, playbackUrl)
    );
}
